import buildVersion from '../buildVersion';

const VERSION_KEY = 'dateLoveClubAppVersion';
const RELOAD_KEY = 'dateLoveClubVersionReload';
const CHECK_INTERVAL = 60 * 1000;

function removeApplicationCacheEntries() {
  const protectedKeys = new Set([
    'token', 'userInfo', 'baseInfo', 'lang', 'change-language', 'language', VERSION_KEY
  ]);
  const removable = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key || protectedKeys.has(key)) continue;
    if (/cache|cached|lottery_|failed_images|home_entry/i.test(key)) removable.push(key);
  }
  removable.forEach(key => localStorage.removeItem(key));
}

async function clearBrowserCaches() {
  removeApplicationCacheEntries();
  if ('caches' in window) {
    try {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    } catch (error) {
      // Cache cleanup must never prevent the application from starting.
    }
  }
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    } catch (error) {
      // Some embedded browsers deny service worker management.
    }
  }
  if ('indexedDB' in window) {
    try {
      await new Promise(resolve => {
        const request = indexedDB.open('MihuanGameCache', 1);
        request.onerror = () => resolve();
        request.onsuccess = () => {
          const db = request.result;
          const stores = Array.from(db.objectStoreNames);
          if (!stores.length) { db.close(); resolve(); return; }
          const transaction = db.transaction(stores, 'readwrite');
          stores.forEach(name => transaction.objectStore(name).clear());
          transaction.oncomplete = () => { db.close(); resolve(); };
          transaction.onerror = () => { db.close(); resolve(); };
        };
      });
    } catch (error) {
      // Private browsing modes may reject IndexedDB access.
    }
  }
}

function reloadForVersion(version) {
  if (sessionStorage.getItem(RELOAD_KEY) === version) return false;
  sessionStorage.setItem(RELOAD_KEY, version);
  const url = new URL(window.location.href);
  url.searchParams.set('_appv', version);
  window.location.replace(url.toString());
  return true;
}

async function fetchLatestVersion() {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-cache' }
  });
  if (!response.ok) throw new Error(`Version request failed: ${response.status}`);
  const data = await response.json();
  return String(data.version || '');
}

export async function ensureLatestVersion() {
  try {
    const latestVersion = await fetchLatestVersion();
    const installedVersion = localStorage.getItem(VERSION_KEY);
    if (!installedVersion) {
      localStorage.setItem(VERSION_KEY, buildVersion);
      return true;
    }
    if (latestVersion && (latestVersion !== buildVersion || installedVersion !== buildVersion)) {
      await clearBrowserCaches();
      localStorage.setItem(VERSION_KEY, latestVersion);
      // Reload only once for the same target version. If a CDN is briefly
      // inconsistent, start the current bundle instead of leaving a black page.
      if (reloadForVersion(latestVersion)) return false;
      return true;
    }
    localStorage.setItem(VERSION_KEY, buildVersion);
    sessionStorage.removeItem(RELOAD_KEY);
  } catch (error) {
    console.warn('Version check skipped:', error.message);
  }
  return true;
}

export function startVersionWatcher() {
  window.setInterval(async () => {
    try {
      const latestVersion = await fetchLatestVersion();
      if (latestVersion && latestVersion !== buildVersion) {
        await clearBrowserCaches();
        localStorage.setItem(VERSION_KEY, latestVersion);
        reloadForVersion(latestVersion);
      }
    } catch (error) {
      // A temporary network failure must not interrupt the current page.
    }
  }, CHECK_INTERVAL);
}
